import React, { useMemo, useRef, useState } from 'react';
import copy from 'copy-to-clipboard';
import { LogClick, useClickStreamContext } from 'features/shared/ClickStream';
import ModalV2 from 'features/shared/ModalV2';
import { CheckIcon } from '@gr-monorepo/common/public/assets/icons';
import { ReactComponent as CopyIcon } from 'features/shared/RatingImproveResultPopup/copy.svg';
import { PrimaryButton, SecondaryButton } from '@gr-monorepo/common/public/ui/Buttons';
import useUserData from 'hooks/useUserData';
import { useAuthContext } from 'hooks/useAuthContext';
import { getCopyText, getTextPopup, processInformText } from './utils';
import * as S from './OlympiadInformModalStyled';

const InformModal = ({ text, onClose }: { text: string; onClose: () => void }) => {
  const { logPageEvent } = useClickStreamContext();
  const textRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const textPopup = useMemo(() => getTextPopup(text), [text]);

  const handleCopy = () => {
    logPageEvent?.({
      eventInfo: `Олимпиада рейтинг: Клик по кнопке скопировать`,
      eventType: `olymp_rating_click_copy`,
    });
    copy(getCopyText(textRef.current?.innerText || '', textPopup));
    setCopied(true);
  };

  const handleClose = () => {
    logPageEvent?.({
      eventInfo: `Олимпиада рейтинг: Клик по вне попапа (закрытие)`,
      eventType: `olymp_rating_click_outside_close`,
    });
    onClose();
  };

  return (
    <ModalV2 onClose={handleClose}>
      <S.ModalContent data-testid="olympiad-inform-modal">
        <S.ModalTitle>
          Отправьте сообщение <br /> образовательным организациям
        </S.ModalTitle>
        <S.ModalTextWrap>
          <S.ModalText ref={textRef} dangerouslySetInnerHTML={{ __html: textPopup }} />
        </S.ModalTextWrap>
        <S.CopyActions $copied={copied}>
          {copied ? (
            <SecondaryButton type="button" size="xlarge" onClick={handleCopy} data-testid="olympiad-inform-modal-copy-button">
              <CheckIcon />
              Скопировано
            </SecondaryButton>
          ) : (
            <PrimaryButton type="button" size="xlarge" onClick={handleCopy} data-testid="olympiad-inform-modal-copy-button">
              <CopyIcon />
              Скопировать
            </PrimaryButton>
          )}
        </S.CopyActions>
      </S.ModalContent>
    </ModalV2>
  );
};

const OlympiadInformButton = ({ text }: { text: string }) => {
  const { authorized } = useAuthContext();
  const userData = useUserData();
  const [showPopup, setShowPopup] = useState(false);

  const processedText = useMemo(
    () => processInformText(text, userData?.id),
    [text, userData?.id],
  );

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setShowPopup(true);
  };

  if (!authorized || !processedText) {
    return null;
  }

  return (
    <>
      <LogClick
        eventType="olymp_click_information_link"
        eventInfo="Олимпиада: Клик по кнопке проинформировать"
      >
        <PrimaryButton
          size="medium"
          onClick={handleClick}
          style={{ whiteSpace: 'nowrap' }}
          data-testid="olympiad-inform-button"
        >
          Проинформировать
        </PrimaryButton>
      </LogClick>
      {showPopup && (
        <span onClick={(event) => event.stopPropagation()}>
          <InformModal text={processedText} onClose={() => setShowPopup(false)} />
        </span>
      )}
    </>
  );
};

export default OlympiadInformButton;
